import React, { useRef, useState } from "react";
import { DataTable, DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  useLinkedInLocationMappings,
  useCreateLinkedinLocationMappingMutation,
  useDeleteLinkedinLocationMappingMutation,
} from "../../../hooks/use-query-linkedinlocationmapping";

const LinkedinLocationMappingGrid: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [originalLocation, setOriginalLocation] = useState("");
  const [standardizedLocation, setStandardizedLocation] = useState("");

  const [lazyParams, setLazyParams] = useState({
    sortBy: "originalLocation",
    sortOrder: "asc",
    limit: 10,
    page: 1,
    filters: {} as Record<string, string>,
  });

  const [filters, setFilters] = useState<any>({
    originalLocation: { value: null, matchMode: "contains" },
    standardizedLocation: { value: null, matchMode: "contains" },
  });

  React.useEffect(() => {
    const activeFilters = Object.fromEntries(
      Object.entries(filters)
        .map(([key, val]: [string, any]) => {
          return [key, val?.value ?? ""];
        })
        .filter(([_, value]) => value !== null && value !== undefined && value !== "")
    );

    setLazyParams((prev) => ({
      ...prev,
      page: 1,
      filters: activeFilters,
    }));
  }, [filters]);

  const { data: mappingsRes, isLoading } = useLinkedInLocationMappings({
    page: lazyParams.page,
    limit: lazyParams.limit,
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    ...lazyParams.filters,
  });
  const createMutation = useCreateLinkedinLocationMappingMutation();
  const deleteMutation = useDeleteLinkedinLocationMappingMutation();

  const mappingsData = mappingsRes?.data?.data || [];

  const handleAdd = async () => {
    if (!originalLocation.trim() || !standardizedLocation.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "Todos os campos são obrigatórios.",
        life: 3000,
      });
      return;
    }

    try {
      await createMutation.mutateAsync({
        originalLocation: originalLocation.trim(),
        standardizedLocation: standardizedLocation.trim(),
        lastModifiedBy: "admin@nimber.com", // Em produção, obter do contexto de auth
      });

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Mapeamento de localização criado com sucesso.",
        life: 3000,
      });

      setOriginalLocation("");
      setStandardizedLocation("");
      setShowDialog(false);
    } catch (err: any) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: err.message || "Erro ao adicionar mapeamento.",
        life: 3000,
      });
    }
  };

  const handleDelete = (id: string) => {
    confirmDialog({
      message: "Tem a certeza que deseja remover este mapeamento de localização?",
      header: "Confirmação de Exclusão",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteMutation.mutateAsync(id);
          toast.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "Mapeamento removido com sucesso.",
            life: 3000,
          });
        } catch (err: any) {
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: "Erro ao remover mapeamento.",
            life: 3000,
          });
        }
      },
    });
  };

  const actionsTemplate = (rowData: any) => {
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-text"
        onClick={() => handleDelete(rowData.id)}
        tooltip="Remover Mapeamento"
      />
    );
  };

  const header = (
    <div className="flex justify-content-between align-items-center flex-wrap gap-2">
      <h3 className="m-0">Padronização de Cidades</h3>
      <Button
        label="Adicionar Regra"
        icon="pi pi-plus"
        severity="success"
        onClick={() => setShowDialog(true)}
      />
    </div>
  );

  return (
    <div className="card">
      <Toast ref={toast} />
      <ConfirmDialog />

      <DataTable
        value={mappingsData}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={mappingsRes?.data?.count || 0}
        loading={isLoading}
        header={header}
        emptyMessage="Nenhuma regra de padronização configurada."
        responsiveLayout="scroll"
        onPage={(e: DataTablePageEvent) =>
          setLazyParams((prev) => ({
            ...prev,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          }))
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams((prev) => ({
            ...prev,
            sortBy: e.sortField,
            sortOrder: e.sortOrder === -1 ? "desc" : "asc",
          }))
        }
        filters={filters}
        onFilter={(e) => setFilters(e.filters)}
        rowsPerPageOptions={[5, 10, 25, 50]}
        dataKey="id"
      >
        <Column field="originalLocation" header="Local Original do LinkedIn" sortable filter showFilterMatchModes={false} />
        <Column field="standardizedLocation" header="Cidade Padronizada" sortable filter showFilterMatchModes={false} />
        <Column
          header="Data de Cadastro"
          field="createdAt"
          body={(row) => new Date(row.createdAt).toLocaleDateString()}
          sortable
        />
        <Column body={actionsTemplate} header="Ações" style={{ width: "8rem", textAlign: "center" }} />
      </DataTable>

      <Dialog
        header="Adicionar Regra de Padronização"
        visible={showDialog}
        style={{ width: "450px" }}
        modal
        onHide={() => setShowDialog(false)}
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setShowDialog(false)}
            />
            <Button
              label="Guardar"
              icon="pi pi-check"
              onClick={handleAdd}
              loading={createMutation.isPending}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3 mt-2">
          <div className="flex flex-column gap-1">
            <label htmlFor="originalLocation" className="font-bold">Local Original (como vem do LinkedIn)</label>
            <InputText
              id="originalLocation"
              value={originalLocation}
              onChange={(e) => setOriginalLocation(e.target.value)}
              placeholder="Ex: Lisbon, Portugal ou Greater Lisbon Area"
              className="w-full"
              autoFocus
            />
          </div>

          <div className="flex flex-column gap-1">
            <label htmlFor="standardizedLocation" className="font-bold">Cidade Padronizada (como deve ficar)</label>
            <InputText
              id="standardizedLocation"
              value={standardizedLocation}
              onChange={(e) => setStandardizedLocation(e.target.value)}
              placeholder="Ex: Lisboa"
              className="w-full"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default LinkedinLocationMappingGrid;
